import { useCallback } from 'react';

import { sum } from 'lodash';

import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { propertyChangesToProjectChanges } from '@shared/helpers/pipeline/property-change-to-project-change.helpers';
import {
  Co2eInputUnits,
  getConversionFactorValue,
  SupportedConversionUnits,
} from '@shared/helpers/conversion-factors.helpers';
import {
  getCount,
  getElementProperty,
  isElementExpressionProperty,
} from '@shared/helpers/element-property.helpers';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';
import { solve } from '@shared/helpers/expression-solving.helpers';
import {
  type ExpressionVariablesRecord,
  getVariablesById,
} from '@shared/helpers/expression-variables.helpers';
import { getCombinedConversionFactors } from '@shared/helpers/results.helpers';
import {
  applyProductElementOverrides,
  getGenericProductId,
  getProductElementData,
  getProductOverride,
  hasCarbonLevelOverride,
  resolveProductFromMap,
} from '@shared/helpers/product-element.helpers';
import { reusedContentProductId } from '@shared/templates/categories/reused-content-property.model';
import { labourProductMap } from '@shared/templates/categories/labour/labour.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  type IProductElement,
  type OneOfPropertyElements,
  type PercentileFactorRange,
  ProductElementOverrideTarget,
} from '@shared/models/project.model';
import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  type ProductID,
  type ProductRecord,
} from '@shared/models/product.model';
import { type SelectableQuantityUnit } from '@shared/models/unit.model';
import { type DivideByOption } from '@shared/helpers/display/get-display-value-data.helper';
import { isSelectableQuantityUnit } from '@shared/helpers/unit.helpers';

import { type PerUnitOptionValue } from '../components/SelectPerUnit';
import {
  getProductEstimates,
  percentileToFactor,
} from '@shared/helpers/product-market-estimates.helpers';
import type { ProjectMarketEstimates } from '@shared/models/project.model';

export type CombinedValue = {
  /** True when values differ across elements/products. */
  mixed: boolean;
  /** Mean of base (non-overridden) values; */
  baseAverage: number;
  /** Fallback value when all values are identical; undefined when mixed. */
  fallbackValue?: number;
  /** Mean of values after applying overrides. */
  adjustedAverage: number;
  /** True when at least one fallback value was present. */
  hasFallbackValue: boolean;
};

type Co2eAdjustmentValue = {
  mixed: boolean;
  average: number;
};

type NormalizedValue = {
  average: number;
};

type EpdSelection = {
  mixed: boolean;
  id: ProductID | undefined;
};

export const defaultPerUnit: PerUnitOptionValue = 'total';

export const { useStore: usePreferredPerUnit, set: setPreferredPerUnit } =
  createLocalStorageStore<PerUnitOptionValue>(
    'preferred_product_bulk_edit_per_unit',
    defaultPerUnit,
  );

type AggregateMetricsInput = {
  /** Collected numeric values used for aggregation. */
  values: number[];
};

type AggregateMetricsOutput = {
  /** Mean value across the provided values. */
  average: number;
  /** True when values differ across inputs. */
  mixed: boolean;
};

const getAggregateMetrics = ({
  values,
}: AggregateMetricsInput): AggregateMetricsOutput => {
  const valuesSet = new Set(values);
  const average = values.length === 0 ? 0 : sum(values) / values.length;
  return {
    average,
    mixed: valuesSet.size > 1,
  };
};

const getResolvedElementCount = (
  element: IProductElement,
): number | undefined => {
  const count = getCount(element, false);
  if (isExpressionValue(count)) return count.resolved;
  return guards.is.number(count) ? count : undefined;
};

const getTargetForOverride = (
  property: SupportedConversionUnits,
): ProductElementOverrideTarget | undefined => {
  if (property === 'co2e_transport')
    return ProductElementOverrideTarget.TRANSPORT_KM;
  if (property === 'co2e_waste_percent')
    return ProductElementOverrideTarget.WASTE_PERCENT;
  if (property === 'co2e_A1-A3') return ProductElementOverrideTarget.CO2E_A1_A3;
  if (property === 'sek_A1-A3') return ProductElementOverrideTarget.SEK_A1_A3;
  return undefined;
};

type ElementFactors = {
  combined: ReturnType<typeof getCombinedConversionFactors>;
  baseFactor: number;
  adjustedFactor: number;
  hasOverride: boolean;
};

const getElementFactors = ({
  productsMap,
  productElement,
  property,
  overrideTarget,
  marketEstimates,
}: {
  productsMap: ProductRecord | undefined;
  productElement: IProductElement;
  property: SupportedConversionUnits;
  overrideTarget?: ProductElementOverrideTarget;
  marketEstimates?: ProjectMarketEstimates;
}): ElementFactors => {
  const data = getProductElementData(productElement);
  const epdProduct =
    data.type === 'epd'
      ? resolveProductFromMap(productsMap, data.epdId)
      : undefined;
  const genericProduct = resolveProductFromMap(productsMap, data.genericId);
  const combined = getCombinedConversionFactors(
    epdProduct,
    genericProduct,
    productElement.unit,
  );
  const baseFactor = getConversionFactorValue(combined, property);

  const target = overrideTarget ?? getTargetForOverride(property);
  const hasOverride = target
    ? guards.is.defined(getProductOverride(productElement, target))
    : false;

  const estimate = getProductEstimates(marketEstimates, data.genericId);
  const adjusted = genericProduct
    ? applyProductElementOverrides(
        genericProduct,
        productElement.productOverrides,
        estimate,
        epdProduct ? { epdProduct, unit: productElement.unit } : undefined,
      )
    : undefined;
  const adjustedFactor = adjusted
    ? getConversionFactorValue(
        epdProduct
          ? adjusted.conversion_factors
          : getCombinedConversionFactors(
              undefined,
              adjusted,
              productElement.unit,
            ),
        property,
      )
    : baseFactor;

  return { combined, baseFactor, adjustedFactor, hasOverride };
};

/**
 * @param perUnit 'total' uses summed totals, 'GFA' divides totals by gfa,
 * and quantity units use quantity-weighted averages per selected unit.
 * @param gfa Only used when perUnit is 'GFA'.
 */
export const getPropertyValuesFromProducts = (
  productsMap: ProductRecord | undefined,
  productElements: IProductElement[],
  property: SupportedConversionUnits,
  perUnit: PerUnitOptionValue,
  gfa?: number,
  marketEstimates?: ProjectMarketEstimates,
): CombinedValue & {
  hasExplicitValue: boolean;
  fromItems: IProductElement[];
  hasCompatibleItems: boolean;
} => {
  let totalBase = 0;
  let totalAdjusted = 0;
  let totalQuantity = 0;
  let weightedBaseSum = 0;
  let weightedAdjustedSum = 0;
  const baseValues: number[] = [];
  const adjustedValues: number[] = [];
  const fromItems: IProductElement[] = [];

  let hasExplicitValue = false;
  productElements.forEach((productElement) => {
    if (isProductExcludedFromProperty(productElement, property)) return;

    const count = getResolvedElementCount(productElement);
    if (!guards.is.number(count)) return;
    const { baseFactor, adjustedFactor, hasOverride, combined } =
      getElementFactors({
        productsMap,
        productElement,
        property,
        marketEstimates,
      });
    if (!guards.is.number(baseFactor)) return;
    fromItems.push(productElement);
    if (hasOverride) hasExplicitValue = true;

    if (isSelectableQuantityUnit(perUnit)) {
      const quantityFactor = getConversionFactorValue(combined, perUnit);
      if (!guards.is.number(quantityFactor) || quantityFactor === 0) return;
      const quantity = count * quantityFactor;
      const basePerUnit = baseFactor / quantityFactor;
      const adjustedPerUnit = adjustedFactor / quantityFactor;
      totalQuantity += quantity;
      weightedBaseSum += basePerUnit * quantity;
      weightedAdjustedSum += adjustedPerUnit * quantity;
      baseValues.push(basePerUnit);
      adjustedValues.push(adjustedPerUnit);
      return;
    }
    const baseTotal = baseFactor * count;
    const adjustedTotal = adjustedFactor * count;
    totalBase += baseTotal;
    totalAdjusted += adjustedTotal;
    baseValues.push(baseTotal);
    adjustedValues.push(adjustedTotal);
  });

  const baseMetrics = getAggregateMetrics({
    values: baseValues,
  });
  const adjustedMetrics = getAggregateMetrics({
    values: adjustedValues,
  });

  const getAggregateValue = (total: number) => {
    if (perUnit === 'total') return total;
    if (perUnit === 'GFA') return gfa ? total / gfa : 0;
    if (totalQuantity === 0) return 0;
    return total / totalQuantity;
  };
  const baseAverage = isSelectableQuantityUnit(perUnit)
    ? getAggregateValue(weightedBaseSum)
    : getAggregateValue(totalBase);
  const adjustedAverage = isSelectableQuantityUnit(perUnit)
    ? getAggregateValue(weightedAdjustedSum)
    : getAggregateValue(totalAdjusted);
  return {
    baseAverage,
    fallbackValue: baseMetrics.mixed ? undefined : baseAverage,
    adjustedAverage,
    mixed: adjustedMetrics.mixed,
    hasFallbackValue: baseValues.length > 0,
    hasExplicitValue,
    fromItems,
    hasCompatibleItems: fromItems.length > 0,
  };
};

const getNormalizedPropertyValueFromProducts = ({
  productsMap,
  productElements,
  property,
  perUnit,
  gfa,
  useEpd,
}: {
  productsMap: ProductRecord | undefined;
  productElements: IProductElement[];
  property: Co2eInputUnits | 'sek_A1-A3' | 'co2e_A1-A3';
  perUnit: PerUnitOptionValue;
  gfa?: number;
  useEpd: boolean;
}): NormalizedValue => {
  let totalValue = 0;
  let totalQuantity = 0;
  let weightedSum = 0;

  productElements.forEach((productElement) => {
    const count = getResolvedElementCount(productElement);
    if (!guards.is.number(count)) return;
    const data = getProductElementData(productElement);
    const epdProduct =
      data.type === 'epd'
        ? resolveProductFromMap(productsMap, data.epdId)
        : undefined;
    const genericProduct = resolveProductFromMap(productsMap, data.genericId);
    const factors = useEpd
      ? getCombinedConversionFactors(
          epdProduct,
          genericProduct,
          productElement.unit,
        )
      : getCombinedConversionFactors(
          undefined,
          genericProduct,
          productElement.unit,
        );
    const factor = getConversionFactorValue(factors, property);
    if (!guards.is.number(factor)) return;

    if (isSelectableQuantityUnit(perUnit)) {
      const quantityFactor = getConversionFactorValue(factors, perUnit);
      if (!guards.is.number(quantityFactor) || quantityFactor === 0) return;
      const quantity = count * quantityFactor;
      const perUnitValue = factor / quantityFactor;
      totalQuantity += quantity;
      weightedSum += perUnitValue * quantity;
      return;
    }

    const total = factor * count;
    totalValue += total;
  });

  const getAggregateValue = (total: number) => {
    if (perUnit === 'total') return total;
    if (perUnit === 'GFA') return gfa ? total / gfa : 0;
    if (totalQuantity === 0) return 0;
    return total / totalQuantity;
  };

  return {
    average: isSelectableQuantityUnit(perUnit)
      ? getAggregateValue(weightedSum)
      : getAggregateValue(totalValue),
  };
};

/**
 * Returns the percent delta between EPD-derived and generic product values for
 * the requested {@link Co2eInputUnits} or currency property.
 * When the generic average is missing or zero, the delta is `0`.
 */
/**
 * Conversion factor shown in the product bulk-edit side panel (per selected unit).
 * With a single EPD, {@link CombinedValue.adjustedAverage} already includes EPD
 * factors; applying a generic-vs-EPD delta on {@link CombinedValue.baseAverage}
 * would double-count.
 */
export const getBulkEditConversionFactorDisplayValue = (
  propertyData: Pick<CombinedValue, 'baseAverage' | 'adjustedAverage'>,
  options: { useEpdFactors: boolean; adjustmentMultiplier: number },
): number => {
  if (options.useEpdFactors) return propertyData.adjustedAverage;
  return propertyData.baseAverage * options.adjustmentMultiplier;
};

export const getEPDPropertyDelta = ({
  productsMap,
  productElements,
  property,
  perUnit,
  gfa,
}: {
  productsMap: ProductRecord | undefined;
  productElements: IProductElement[];
  property: Co2eInputUnits | 'sek_A1-A3' | 'co2e_A1-A3';
  perUnit: PerUnitOptionValue;
  gfa?: number;
}): { value: number } => {
  const genericValue = getNormalizedPropertyValueFromProducts({
    productsMap,
    productElements,
    property,
    perUnit,
    gfa,
    useEpd: false,
  });
  const epdValue = getNormalizedPropertyValueFromProducts({
    productsMap,
    productElements,
    property,
    perUnit,
    gfa,
    useEpd: true,
  });
  if (!Number.isFinite(genericValue.average) || genericValue.average === 0) {
    return { value: 0 };
  }
  const value =
    ((epdValue.average - genericValue.average) / genericValue.average) * 100;
  return { value: Number.isFinite(value) ? value : 0 };
};

/**
 * Get CO2e adjustment values for display.
 * Only considers elements that participate in carbon/cost (excludes e.g. reused construction).
 * Matches the set we write to via the slider so the displayed value round-trips.
 * Note: With marketPercentileMultiplier type, the stored value is a percentile.
 */
export const getCo2eAdjustmentValues = (
  productElements: IProductElement[],
): Co2eAdjustmentValue => {
  const carbonElements = productElements.filter(
    (el) => !isProductExcludedFromProperty(el, 'co2e_A1-A3'),
  );
  const values = carbonElements.map((productElement) => {
    const entry = getProductOverride(
      productElement,
      ProductElementOverrideTarget.CO2E_A1_A3,
    );
    if (!entry) return 50; // Default to midpoint (no adjustment)
    const { override } = entry;
    if (override.type === 'marketPercentileMultiplier') {
      // Return the percentile value - caller should convert to percent using market estimates
      return override.value;
    }
    // Legacy 'multiply' type - convert multiplier to approximate midpoint
    // This is a fallback for old data; returns 50 (midpoint) as a safe default
    if ((override as { type: string }).type === 'multiply') {
      return 50; // Legacy data treated as "no adjustment" for UI purposes
    }
    return 50; // Default to midpoint
  });

  const metrics = getAggregateMetrics({
    values,
  });
  return metrics;
};

/**
 * Whether any of the given product elements carries an explicit carbon (A1–A3) adjustment
 * override. Used to decide when the conservative-mode slider fallback (+25% on generic data,
 * DEV-3411) may apply: it only kicks in when there is no user-set override to respect.
 */
export const hasCarbonAdjustmentOverride = (
  productElements: IProductElement[],
): boolean => productElements.some(hasCarbonLevelOverride);

/**
 * Compute the weighted average CO2e and cost factors at a given percentile.
 * Each element's factor is weighted by its base CO2e/cost contribution.
 *
 * @param marketEstimates - Market estimates snapshot (from project or freshly created)
 */
export const getWeightedFactorFromPercentile = (
  productElements: IProductElement[],
  percentile: number,
  productsMap: ProductRecord | undefined,
  marketEstimates: ProjectMarketEstimates,
): { carbonFactor: number; costFactor: number } => {
  let carbonWeightedSum = 0;
  let costWeightedSum = 0;
  let carbonTotalWeight = 0;
  let costTotalWeight = 0;

  const totalCounts = productElements.reduce(
    (sum, productElement) =>
      sum + (getResolvedElementCount(productElement) ?? 0),
    0,
  );

  productElements.forEach((productElement) => {
    // if all counts add up to 0, then we weight with 1 instead, otherwise we get nothing.
    const count =
      totalCounts === 0 ? 1 : getResolvedElementCount(productElement);
    if (!guards.is.number(count) || count === 0) return;

    const { genericId } = getProductElementData(productElement);
    const estimates = getProductEstimates(marketEstimates, genericId);
    if (!estimates) return;

    const { baseFactor: baseCo2e } = getElementFactors({
      productsMap,
      productElement,
      property: 'co2e_A1-A3',
    });
    const { baseFactor: baseCost } = getElementFactors({
      productsMap,
      productElement,
      property: 'sek_A1-A3',
    });

    // Use stored PercentileFactorRange directly (includes percentileRange)
    const carbonFactor = percentileToFactor(percentile, estimates.carbon);
    const costFactor = percentileToFactor(100 - percentile, estimates.cost);

    const co2eWeight = Math.abs((baseCo2e ?? 0) * count);
    const costWeight = Math.abs((baseCost ?? 0) * count);

    carbonWeightedSum += carbonFactor * co2eWeight;
    carbonTotalWeight += co2eWeight;
    costWeightedSum += costFactor * costWeight;
    costTotalWeight += costWeight;
  });

  return {
    carbonFactor:
      carbonTotalWeight > 0 ? carbonWeightedSum / carbonTotalWeight : 0,
    costFactor: costTotalWeight > 0 ? costWeightedSum / costTotalWeight : 0,
  };
};

/**
 * Compute a weighted {@link PercentileFactorRange} from multiple product elements.
 * Weights each product's range by its base CO2e contribution (baseFactor * count).
 * Returns undefined when no valid products with estimates exist.
 */
export const getWeightedPercentileFactorRange = (
  productElements: IProductElement[],
  productsMap: ProductRecord | undefined,
  marketEstimates: ProjectMarketEstimates,
): PercentileFactorRange | undefined => {
  let minFactorWeightedSum = 0;
  let maxFactorWeightedSum = 0;
  let totalWeight = 0;

  productElements.forEach((productElement) => {
    const count = getResolvedElementCount(productElement);
    if (!guards.is.number(count) || count === 0) return;

    const { genericId } = getProductElementData(productElement);
    const estimates = getProductEstimates(marketEstimates, genericId);
    if (!estimates) return;

    const { baseFactor: baseCo2e } = getElementFactors({
      productsMap,
      productElement,
      property: 'co2e_A1-A3',
    });

    const weight = Math.abs((baseCo2e ?? 0) * count);
    if (weight === 0) return;

    minFactorWeightedSum += estimates.carbon.minFactor * weight;
    maxFactorWeightedSum += estimates.carbon.maxFactor * weight;
    totalWeight += weight;
  });

  if (totalWeight === 0) return undefined;

  return {
    percentileRange: { min: 5, max: 95 },
    minFactor: minFactorWeightedSum / totalWeight,
    maxFactor: maxFactorWeightedSum / totalWeight,
  };
};

export const getPerUnitLabel = (unit: PerUnitOptionValue) => {
  if (unit === 'total') return 'Total';
  return `Per ${unit}`;
};

const perUnitDivideByUnitMap: Record<
  SelectableQuantityUnit,
  DivideByOption['unit'] | undefined
> = {
  m: 'm',
  'm²': 'm^2',
  'm³': 'm^3',
  kg: 'kg',
  pcs: 'pcs',
  kWh: 'kWh',
  MJ: 'MJ',
  hr: 'h',
  '%': undefined,
  'kg/m²': '(kg/m^2)',
  'kg/m³': '(kg/m^3)',
};

export const getPerUnitDivideBy = (
  unit: PerUnitOptionValue,
): DivideByOption | undefined => {
  if (unit === 'total' || unit === '%') return undefined;
  if (unit === 'GFA') return { unit: 'm^2' };
  if (!isSelectableQuantityUnit(unit)) return undefined;
  const divideByUnit = perUnitDivideByUnitMap[unit];
  return divideByUnit ? { unit: divideByUnit } : undefined;
};

/**
 * Builds the count patch for a property-slider commit.
 *
 * A cleared override with no underlying expression must remove `count`
 * entirely so the property falls back to `fallbackCount`. Solving an empty
 * expression would instead create `{ resolved: 0 }`, which is invalid for
 * bounded properties such as Content coverage (minimum 0.01).
 */
export const solveElementPropertyCommitCount = (
  value: number | undefined,
  expression: string,
  variables: ExpressionVariablesRecord[string] | undefined,
) => {
  if (value === undefined && expression.trim() === '') return undefined;
  return solve(value, expression, variables);
};

export const useElementPropertyCommitHandler = (
  elements: OneOfPropertyElements[],
  propertyName: ElementPropertyName,
  variablesRecord: ExpressionVariablesRecord,
) => {
  const applyChanges = useApplyProjectChanges();
  return useCallback(
    (value?: number) => {
      const changes = elements.flatMap((element) => {
        const property = getElementProperty(element, propertyName);
        const count = property?.count;
        const expression = isExpressionValue(count) ? count.expression : '';
        const variables = getVariablesById(variablesRecord, element.id);

        const newCount = solveElementPropertyCommitCount(
          value,
          expression,
          variables,
        );
        return propertyChangesToProjectChanges(element, [
          { name: propertyName, count: newCount },
        ]);
      });
      applyChanges(changes);
    },
    [elements, propertyName, applyChanges, variablesRecord],
  );
};

export const getEpdSelection = (
  productElements: IProductElement[],
): EpdSelection => {
  const epdIds = productElements
    .map((element) => getProductElementData(element))
    .map((data) => (data.type === 'epd' ? data.epdId : undefined))
    .filter(guards.is.defined);

  const uniqueIds = new Set(epdIds);
  const hasAnyEpd = epdIds.length > 0;
  const hasMixedEpdIds = uniqueIds.size > 1;
  const hasMissingEpd = epdIds.length !== productElements.length;
  const mixed = hasMixedEpdIds || (hasMissingEpd && hasAnyEpd);
  return mixed ? { mixed, id: undefined } : { mixed, id: epdIds[0] };
};

type BulkPropertyValuesInput<Item> = {
  items: Item[];
  getValues: (item: Item) =>
    | {
        type: 'valid';
        // currentValue should be the active value of the element property.
        currentValue: number;
        explicitValue?: number;
        fallbackValue?: number;
      }
    | { type: 'invalid' };
};

/**
 * Aggregated values for bulk editing.
 * `current` reflects the active value shown to the user (explicit if present,
 * otherwise fallback). `fallback` reflects fallback values only.
 */
export type BulkPropertyValuesOutput<Item> = {
  /** if current OR fallback is mixed */
  mixed: boolean;
  /** Aggregates for the active value per item. */
  current: {
    /** Mean of active values across valid items (0 when none). */
    average: number;
    /** True when active values differ across valid items. */
    mixed: boolean;
  };
  /** Aggregates for fallback values across valid items. */
  fallback: {
    /** Mean of fallback values (0 when none). */
    average: number;
    /** True when fallback values differ across valid items. */
    mixed: boolean;
  };
  /** True when at least one item had an explicit value. */
  hasExplicitValue: boolean;
  /** True when at least one item had a fallback value. */
  hasFallbackValue: boolean;
  /** Items included in aggregation (invalid items are excluded). */
  fromItems: Item[];
  /** True when at least one item passed compatibility filtering. */
  hasCompatibleItems: boolean;
};

const getBulkPropertyValues = <Item>({
  items,
  getValues,
}: BulkPropertyValuesInput<Item>): BulkPropertyValuesOutput<Item> => {
  const fromItems: Item[] = [];
  const values: number[] = [];
  const explicitValues: number[] = [];
  const fallbackValues: number[] = [];

  items.forEach((item) => {
    const itemValues = getValues(item);
    if (itemValues.type === 'invalid') return; // exclude from calculation
    fromItems.push(item);
    const { explicitValue, fallbackValue } = itemValues;

    if (guards.is.number(fallbackValue)) fallbackValues.push(fallbackValue);
    if (guards.is.number(explicitValue)) explicitValues.push(explicitValue);
    values.push(itemValues.currentValue);
  });

  const currentMetrics = getAggregateMetrics({ values });
  const fallbackMetrics = getAggregateMetrics({ values: fallbackValues });
  const hasExplicitValue = explicitValues.length > 0;
  const hasFallbackValue = fallbackValues.length > 0;
  return {
    mixed: currentMetrics.mixed || fallbackMetrics.mixed,
    current: currentMetrics,
    fallback: fallbackMetrics,
    hasExplicitValue,
    hasFallbackValue,
    fromItems,
    hasCompatibleItems: fromItems.length > 0,
  };
};

/** Labour product IDs excluded from waste and transport calculations */
const WASTE_TRANSPORT_EXCLUDED_PRODUCTS = new Set<string>(
  Object.values(labourProductMap),
);

/** Generic product IDs excluded from carbon and cost calculations */
const CARBON_COST_EXCLUDED_PRODUCTS = new Set<string>([reusedContentProductId]);

/**
 * Whether a product element should be excluded from a bulk property calculation.
 * Labour products have no waste/transport.
 * Reused construction products have no carbon/cost.
 */
export const isProductExcludedFromProperty = (
  productElement: IProductElement,
  property: SupportedConversionUnits,
): boolean => {
  const genericId = getGenericProductId(productElement);
  switch (property) {
    case 'co2e_waste_percent':
    case 'co2e_transport':
      return WASTE_TRANSPORT_EXCLUDED_PRODUCTS.has(genericId);
    case 'co2e_A1-A3':
    case 'sek_A1-A3':
      return CARBON_COST_EXCLUDED_PRODUCTS.has(genericId);
    default:
      return false;
  }
};

export const getBulkPropertyValuesFromProductElements = ({
  productsMap,
  productElements,
  property,
  marketEstimates,
}: {
  productsMap: ProductRecord | undefined;
  productElements: IProductElement[];
  property: Co2eInputUnits | 'sek_A1-A3' | 'co2e_A1-A3';
  marketEstimates?: ProjectMarketEstimates;
}): BulkPropertyValuesOutput<IProductElement> => {
  const overrideTarget = getTargetForOverride(property);
  return getBulkPropertyValues({
    items: productElements,
    getValues: (productElement) => {
      if (isProductExcludedFromProperty(productElement, property))
        return { type: 'invalid' };

      const { baseFactor, adjustedFactor, hasOverride } = getElementFactors({
        productsMap,
        productElement,
        property,
        overrideTarget,
        marketEstimates,
      });
      return {
        type: 'valid',
        currentValue: adjustedFactor,
        // there's only a explicit value if there's an override
        explicitValue: hasOverride ? adjustedFactor : undefined,
        fallbackValue: baseFactor,
      };
    },
  });
};

export const getBulkPropertyValuesFromPropertyElements = ({
  elements,
  propertyName,
  variablesRecord,
}: {
  elements: OneOfPropertyElements[];
  propertyName:
    | ElementPropertyName.Lifetime
    | ElementPropertyName.ReusedContent
    | ElementPropertyName.ContentCoverage;
  variablesRecord: ExpressionVariablesRecord;
}): BulkPropertyValuesOutput<OneOfPropertyElements> => {
  return getBulkPropertyValues({
    items: elements,
    getValues: (element) => {
      const property = getElementProperty(element, propertyName);
      if (!isElementExpressionProperty(property)) return { type: 'invalid' };

      // Explicit = user-set .value on count (only thing this form can add/remove).
      // Fallback chain: re-solved expression (without .value) → fallbackCount.
      // Re-solving is needed because .resolved reflects .value when set,
      // hiding the expression result we need to display as fallback.
      const explicitValue = property.count?.value;
      const variables = getVariablesById(variablesRecord, element.id);
      const countExpression = property.count?.expression;
      const resolvedExpression =
        guards.is.string(countExpression) &&
        guards.is.nonEmptyString(countExpression)
          ? solve(undefined, countExpression, variables)
          : undefined;
      const fallbackValue =
        resolvedExpression?.resolved ?? property.fallbackCount?.resolved;

      const currentValue = explicitValue ?? fallbackValue;

      if (!guards.is.number(currentValue)) return { type: 'invalid' };

      return {
        type: 'valid',
        currentValue,
        explicitValue,
        fallbackValue,
      };
    },
  });
};
